import { DeleteUserRequest, DeleteUserResponse } from "api/users/deleteUser";
import baseApi from ".";
import createQueryFn from "../createQueryFn";
import api from "api";
import { GetAllUsersRequest, GetAllUsersResponse } from "api/users/getAllUsers";
import {
  GetCompletedChecklistsResponse,
  GetCompletedChecklistsRequest,
} from "api/users/getCompletedChecklists";
import { GetInstallersResponse } from "api/users/getInstallers";
import { GetUserByIdRequest, GetUserByIdResponse } from "api/users/getUserById";
import { GetUserStatisticsResponse, GetUserStatisticsRequest } from "api/users/getUserStatistics";
import { UpdateUserRequest, UpdateUserResponse } from "api/users/updateUser";
import { ApproveUserRequest, ApproveUserResponse } from "api/users/approveUser";
import { CreateUserResponse, CreateUserRequest } from "api/users/createUser";
import { GetMyBookingsResponse } from "api/users/getMyBookings";
import { GetExternalInstallersResponse } from "api/users/getExternalInstallers";
import { GetAllUsersLimitedResponse } from "api/users/getAllUsersLimited";

const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserById: builder.query<GetUserByIdResponse, GetUserByIdRequest>({
      queryFn: createQueryFn(api.users.getUserById),
      providesTags: (result, error, { userId }) => [{ type: "User", id: userId }],
    }),

    getAllUsers: builder.query<GetAllUsersResponse, GetAllUsersRequest | void>({
      queryFn: createQueryFn(api.users.getAllUsers),
      providesTags: [{ type: "User", id: "LIST" }],
    }),

    getAllUsersLimited: builder.query<GetAllUsersLimitedResponse, GetAllUsersRequest | void>({
      queryFn: createQueryFn(api.users.getAllUsersLimited),
      providesTags: [{ type: "User", id: "LIST" }],
    }),

    getInstallers: builder.query<GetInstallersResponse, void>({
      queryFn: createQueryFn(api.users.getInstallers),
    }),

    getUserStatistics: builder.query<GetUserStatisticsResponse, GetUserStatisticsRequest>({
      queryFn: createQueryFn(api.users.getUserStatistics),
    }),

    getCompletedChecklists: builder.query<
      GetCompletedChecklistsResponse,
      GetCompletedChecklistsRequest
    >({
      queryFn: createQueryFn(api.users.getCompletedChecklists),
    }),

    updateUser: builder.mutation<UpdateUserResponse, UpdateUserRequest>({
      queryFn: createQueryFn(api.users.updateUser),
      invalidatesTags: (result, error, { id }) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    deleteUser: builder.mutation<DeleteUserResponse, DeleteUserRequest>({
      queryFn: createQueryFn(api.users.deleteUser),
      invalidatesTags: (result, error, { userId }) => [
        { type: "User", id: userId },
        { type: "User", id: "LIST" },
      ],
    }),

    approveUser: builder.mutation<ApproveUserResponse, ApproveUserRequest>({
      queryFn: createQueryFn(api.users.approveUser),
      invalidatesTags: (result, error, { userId }) => [
        { type: "User", id: userId },
        { type: "User", id: "LIST" }],
    }),

    createUser: builder.mutation<CreateUserResponse, CreateUserRequest>({
      queryFn: createQueryFn(api.users.createUser),
      invalidatesTags: [{ type: "User", id: "LIST" }],
    }),

    getMyBookings: builder.query<GetMyBookingsResponse, void>({
      queryFn: createQueryFn(api.users.getMyBookings),
      providesTags: [{ type: "Booking", id: "LIST" }],
    }),

    getExternalInstallers: builder.query<GetExternalInstallersResponse, void>({
      queryFn: createQueryFn(api.users.getExternalInstallers),
    }),
  }),
});

export const {
  useGetUserByIdQuery,
  useGetAllUsersQuery,
  useGetAllUsersLimitedQuery,
  useGetInstallersQuery,
  useGetUserStatisticsQuery,
  useGetCompletedChecklistsQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useApproveUserMutation,
  useCreateUserMutation,
  useGetMyBookingsQuery,
  useGetExternalInstallersQuery,
} = usersApi;

export default usersApi;
